/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  accessAPI,
  Boton,
  Titulo,
  Formulario,
  Sidebar,
  CampoInformacion,
  formatFecha,
tituloBase,
  TablaFiltrable } from "../../../shared";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Skeleton } from "primereact/skeleton";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";

const SEARCH_FIELDS = ["numero", "productoEtiqueta"];

export default function Caja() {
  const { cajaId } = useParams();
  const [caja, setCaja] = useState(null);
  const [sidebarEditar, setSidebarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [loaderDesactivar, setLoaderDesactivar] = useState(false);
  const toast = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    cargar();
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cajaId]);

  function cargar() {
    accessAPI(
      "GET",
      `admin/caja/${cajaId}`,
      null,
      (resp) => {
        setCaja(resp);
        document.title = `Caja ${resp.numero}`;
      },
      (resp) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: resp[0]?.msg || "Error al cargar caja",
          sticky: true,
        });
      },
    );
  }

  function guardar(campos) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/caja/${cajaId}`,
      campos,
      () => {
        setLoaderEditar(false);
        setSidebarEditar(false);
        cargar();
      },
      (resp) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: resp[0]?.msg || "Error al guardar",
          sticky: true,
        });
      },
    );
  }

  function desactivar() {
    confirmDialog({
      message:
        "¿Está seguro que desea desactivar esta caja? Sólo se puede desactivar una caja vacía; se conserva el registro histórico.",
      header: "Confirmar desactivación",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Desactivar",
      rejectLabel: "Cancelar",
      accept: () => {
        setLoaderDesactivar(true);
        accessAPI(
          "POST",
          `admin/caja/${cajaId}/desactivar`,
          {},
          () => {
            setLoaderDesactivar(false);
            cargar();
          },
          (resp) => {
            setLoaderDesactivar(false);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: resp[0]?.msg || "Error al desactivar",
              sticky: true,
            });
          },
        );
      },
    });
  }

  function activar() {
    setLoaderDesactivar(true);
    accessAPI(
      "POST",
      `admin/caja/${cajaId}/activar`,
      {},
      () => {
        setLoaderDesactivar(false);
        cargar();
      },
      (resp) => {
        setLoaderDesactivar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: resp[0]?.msg || "Error al activar",
          sticky: true,
        });
      },
    );
  }

  const editarConfig = caja
    ? {
        secciones: [
          {
            nombre: caja.estado === "PENDIENTE" ? "Registrar caja" : "Editar caja",
            campos: [
              {
                nombre: "descripcion",
                tipoitemformularioid: 1,
                etiqueta: "Descripción",
                respuesta: caja.descripcion ?? "",
              },
              {
                nombre: "ubicacion",
                tipoitemformularioid: 1,
                etiqueta: "Ubicación física",
                respuesta: caja.ubicacion ?? "",
              },
              {
                nombre: "notas",
                tipoitemformularioid: 11,
                etiqueta: "Notas",
                respuesta: caja.notas ?? "",
              },
            ],
          },
        ],
        botones: [
          {
            etiqueta:
              caja.estado === "PENDIENTE" ? "Registrar caja" : "Guardar",
            callback: guardar,
            validar: true,
          },
        ],
      }
    : null;

  if (!caja) {
    return (
      <>
        <Skeleton width="10rem" height="2.5rem" css={{ marginTop: "2em" }} />
        <Skeleton width="100%" height="10rem" css={{ marginTop: "1em" }} />
      </>
    );
  }

  const muestraEstadoBody = (m) =>
    m.desactivada ? (
      <Tag value="Desactivada" severity="danger" />
    ) : m.retirada ? (
      <Tag value="Retirada" severity="warning" />
    ) : m.estado === "INGRESADA" ? (
      <Tag value="En caja" severity="success" />
    ) : (
      <Tag value="Pendiente" severity="info" />
    );

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Titulo
        urlVolver="/muestra"
        etiquetas={
          caja.desactivada
            ? [{ texto: "Desactivada", error: true }]
            : caja.estado === "REGISTRADA"
              ? [{ texto: "Registrada", ok: true }]
              : [{ texto: "Pendiente", neutro: true }]
        }
        botones={
          <div css={{ display: "flex", gap: "0.5rem" }}>
            {!caja.desactivada && (
              <Boton callback={() => setSidebarEditar(true)}>
                {caja.estado === "PENDIENTE" ? "Registrar" : "Editar"}
              </Boton>
            )}
            {caja.desactivada ? (
              <Boton callback={activar} loader={loaderDesactivar} verde>
                Activar
              </Boton>
            ) : caja.estado === "REGISTRADA" ? (
              <Boton callback={desactivar} loader={loaderDesactivar} rojo>
                Desactivar
              </Boton>
            ) : null}
          </div>
        }
      >
        Caja {caja.numero}
      </Titulo>

      <Card>
        <Titulo>Información general</Titulo>
        <div className="grid">
          <CampoInformacion nombreCampo="Descripción">
            {caja.descripcion || "—"}
          </CampoInformacion>
          <CampoInformacion nombreCampo="Ubicación">
            {caja.ubicacion || "—"}
          </CampoInformacion>
          {caja.desactivada && (
            <CampoInformacion nombreCampo="Desactivada por">
              {caja.desactivadaAdministrador
                ? `${caja.desactivadaAdministrador.nombre} ${caja.desactivadaAdministrador.apellido}`
                : "—"}
              {caja.fechadesactivacion &&
                ` - ${formatFecha(caja.fechadesactivacion)}`}
            </CampoInformacion>
          )}
          <CampoInformacion nombreCampo="Notas" anchoCompleto>
            {caja.notas || "—"}
          </CampoInformacion>
        </div>
      </Card>

      <Card css={{ marginTop: "1.5rem" }}>
        <Titulo>Muestras en la caja</Titulo>
        <TablaFiltrable
          value={caja.muestras}
          searchFields={SEARCH_FIELDS}
          anchoBusqueda="33%"
          nombreArchivo="muestras_caja"
          emptyMessage="No hay muestras en esta caja"
          stripedRows
          rows={10}
          onRowClick={(e) => {
            const volver = encodeURIComponent(`/caja/${cajaId}`);
            navigate(`/muestra/${e.data.id}?volver=${volver}`);
          }}
          css={{ "& .p-datatable-tbody > tr": { cursor: "pointer" } }}
        >
          <Column header="N°" field="numero" />
          <Column header="Producto" field="productoEtiqueta" />
          <Column header="Estado" body={muestraEstadoBody} />
        </TablaFiltrable>
      </Card>

      <Sidebar
        visible={sidebarEditar}
        onHide={() => setSidebarEditar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        {editarConfig && <Formulario config={editarConfig} loader={loaderEditar} />}
      </Sidebar>
    </>
  );
}
